import { useMemo, useRef, useEffect } from 'react'
import { Chart } from 'chart.js'
import type { TimeSeriesBucket } from '../../types/dashboard'
import { useThemeStore } from '../../stores/themeStore'

interface Props {
    data: TimeSeriesBucket[]
    compareData?: TimeSeriesBucket[]
    granularity: string
    includeReturns: boolean
    comparisonLabel?: string
    /** First day of the primary range (yyyy-MM-dd) — where the x-axis starts when `data` is empty. */
    startDate?: string
}

/** Periods from `from` to `to`: days, 7-day windows or calendar months, per granularity. */
function periodsBetween(from: string, to: string, granularity: string): number {
    if (granularity === 'month') {
        const [fy, fm] = from.split('-').map(Number)
        const [ty, tm] = to.split('-').map(Number)
        return (ty - fy) * 12 + (tm - fm)
    }
    // Date-only strings parse as UTC midnight, so the difference is whole days
    const days = Math.round((Date.parse(to) - Date.parse(from)) / 86_400_000)
    return granularity === 'week' ? Math.floor(days / 7) : days
}

/**
 * A series' sales by x-axis slot, each bucket placed by its period's distance from the series'
 * first bucket rather than by its index, so a missing period can't slide later points left. The
 * server sends a bucket for every period; one missing between two buckets had no sales (0).
 */
function toSlots(series: TimeSeriesBucket[], granularity: string): number[] {
    const slots: number[] = []
    for (const b of series) {
        const slot = periodsBetween(series[0].period, b.period, granularity)
        if (slot < 0) continue
        while (slots.length < slot) slots.push(0)
        slots[slot] = b.sales
    }
    return slots
}

/**
 * Performance line chart that adapts label density based on granularity.
 * Shows primary period with purple gradient + optional comparison period as dashed line.
 * When comparing misaligned ranges, extends the x-axis to the longer dataset.
 */
export default function PerformanceChart({ data, compareData, granularity, includeReturns, comparisonLabel, startDate }: Props) {
    const canvasRef = useRef<HTMLCanvasElement>(null)
    const chartRef = useRef<Chart | null>(null)

    const colorMode = useThemeStore(s => s.getEffectiveColorMode())
    const textColor = colorMode === 'dark' ? 'rgba(255,255,255,0.6)' : '#475569'
    const gridColor = colorMode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'
    const tooltipBg = colorMode === 'dark' ? 'rgba(15,10,30,0.95)' : 'rgba(255,255,255,0.95)'
    const tooltipTitle = colorMode === 'dark' ? '#fff' : '#0f172a'
    const tooltipBody = colorMode === 'dark' ? 'rgba(255,255,255,0.8)' : '#334155'
    const tooltipBorder = colorMode === 'dark' ? 'rgba(168, 85, 247, 0.3)' : 'rgba(168, 85, 247, 0.15)'
    const compLineColor = colorMode === 'dark' ? 'rgba(100, 160, 255, 0.6)' : 'rgba(50, 110, 220, 0.8)'

    const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

    const formatPeriod = (period: string) => {
        if (granularity === 'month') {
            const [y, m] = period.split('-')
            return `${MONTHS[parseInt(m) - 1]} ${String(y).slice(-2)}`
        }
        const [, m, day] = period.split('-')
        return `${MONTHS[parseInt(m) - 1]} ${parseInt(day)}`
    }

    /** Extract month index (0-based) from a period string */
    const getMonth = (period: string): number => {
        const [, m] = period.split('-')
        return parseInt(m) - 1
    }

    /**
     * Period at x-axis index 0, from which every index's period is projected. The server fills each
     * series from its range start, so that is the primary's first bucket. A range that has not
     * started yet arrives empty: fall back to the requested start (week buckets are anchored to it
     * server-side), then to the comparison series. Defined whenever there is an index to label.
     */
    const axisStart = data[0]?.period
        ?? (startDate ? (granularity === 'month' ? startDate.slice(0, 7) : startDate) : undefined)
        ?? compareData?.[0]?.period

    /** Get the period at a given x-axis index, counted from axisStart */
    const getPeriodAt = (idx: number): string => {
        const d = new Date(axisStart! + 'T12:00:00')
        if (granularity === 'month') {
            d.setMonth(d.getMonth() + idx)
        } else if (granularity === 'week') {
            d.setDate(d.getDate() + idx * 7)
        } else {
            d.setDate(d.getDate() + idx)
        }
        return granularity === 'month'
            ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
            : `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
    }

    const primarySlots = useMemo(() => toSlots(data, granularity), [data, granularity])
    const compareSlots = useMemo(() => toSlots(compareData ?? [], granularity), [compareData, granularity])
    const maxLen = Math.max(primarySlots.length, compareSlots.length)

    // Generate labels: show contextual info only on first label and when it changes
    const labels = useMemo(() => {
        const result: string[] = []
        let prevMonth: number | null = null
        let prevYear: number | null = null
        for (let i = 0; i < maxLen; i++) {
            const period = getPeriodAt(i)
            if (granularity === 'month') {
                const [y, m] = period.split('-')
                const month = parseInt(m) - 1
                const year = parseInt(y)
                const shortYear = String(year).slice(-2)
                if (prevYear === null || year !== prevYear) {
                    // First label or new year: show "Mon YY"
                    result.push(`${MONTHS[month]} ${shortYear}`)
                } else {
                    // Same year: show just the month
                    result.push(MONTHS[month])
                }
                prevYear = year
            } else {
                const month = getMonth(period)
                const [, , day] = period.split('-')
                if (prevMonth === null || month !== prevMonth) {
                    // First label or new month: show "Mon DD"
                    result.push(`${MONTHS[month]} ${parseInt(day)}`)
                } else {
                    // Same month: show just the day number
                    result.push(`${parseInt(day)}`)
                }
                prevMonth = month
            }
        }
        return result
    }, [axisStart, maxLen, granularity])

    // Pad primary sales to maxLen (null where primary has no data)
    const salesValues = useMemo(() => {
        const result: (number | null)[] = [...primarySlots]
        while (result.length < maxLen) result.push(null)
        return result
    }, [primarySlots, maxLen])

    // Pad comparison to maxLen (null where comparison has no data)
    const compareValues = useMemo(() => {
        if (compareSlots.length === 0) return null
        const result: (number | null)[] = [...compareSlots]
        while (result.length < maxLen) result.push(null)
        return result
    }, [compareSlots, maxLen])

    useEffect(() => {
        if (!canvasRef.current) return

        if (chartRef.current) {
            chartRef.current.destroy()
            chartRef.current = null
        }

        const ctx = canvasRef.current.getContext('2d')
        if (!ctx) return

        // Gradient for primary sales line
        const gradient = ctx.createLinearGradient(0, 0, 0, canvasRef.current.height)
        gradient.addColorStop(0, 'rgba(168, 85, 247, 0.35)')
        gradient.addColorStop(1, 'rgba(168, 85, 247, 0.02)')

        const maxTicksLimit = granularity === 'day'
            ? Math.min(15, Math.ceil(maxLen / 7))
            : undefined

        const datasets: any[] = [
            {
                label: includeReturns ? 'Net Sales' : 'Gross Sales',
                data: salesValues,
                borderColor: 'rgba(168, 85, 247, 1)',
                backgroundColor: gradient,
                borderWidth: 2,
                pointRadius: maxLen > 60 ? 0 : 3,
                pointHoverRadius: 5,
                pointBackgroundColor: 'rgba(168, 85, 247, 1)',
                fill: true,
                tension: 0.3,
                spanGaps: false,
            },
        ]

        if (compareValues) {
            datasets.push({
                label: comparisonLabel || 'Prior',
                data: compareValues,
                borderColor: compLineColor,
                backgroundColor: 'transparent',
                borderWidth: 1.5,
                borderDash: [5, 4],
                pointRadius: 0,
                pointHoverRadius: 4,
                pointBackgroundColor: 'rgba(50, 110, 220, 1)',
                fill: false,
                tension: 0.3,
                spanGaps: false,
            })
        }

        chartRef.current = new Chart(ctx, {
            type: 'line',
            data: { labels, datasets },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                interaction: { mode: 'index', intersect: false },
                plugins: {
                    legend: {
                        display: true,
                        position: 'top',
                        align: 'end',
                        labels: {
                            color: textColor,
                            font: { size: 11 },
                            boxWidth: 12,
                            padding: 12,
                            usePointStyle: true,
                            pointStyle: 'circle',
                        }
                    },
                    tooltip: {
                        backgroundColor: tooltipBg,
                        titleColor: tooltipTitle,
                        bodyColor: tooltipBody,
                        borderColor: tooltipBorder,
                        borderWidth: 1,
                        cornerRadius: 8,
                        padding: 10,
                        callbacks: {
                            title: (items: any[]) => {
                                if (!items.length) return ''
                                const idx = items[0].dataIndex
                                // Always show full "Mon DD" in tooltip, not abbreviated axis label
                                const fullLabel = formatPeriod(getPeriodAt(idx))
                                if (granularity === 'day') return fullLabel
                                if (granularity === 'week') {
                                    // End of the same 7-day bucket the title starts on
                                    const end = new Date(getPeriodAt(idx) + 'T12:00:00')
                                    end.setDate(end.getDate() + 6)
                                    return `${fullLabel} – ${MONTHS[end.getMonth()]} ${end.getDate()}`
                                }
                                // month
                                return fullLabel
                            },
                            label: (item: any) => {
                                const val = item.parsed.y
                                if (val == null) return ''
                                const formatted = val >= 0
                                    ? `$${val.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`
                                    : `-$${Math.abs(val).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`
                                return `${item.dataset.label}: ${formatted}`
                            }
                        }
                    }
                },
                scales: {
                    x: {
                        grid: { color: gridColor },
                        ticks: {
                            color: textColor,
                            font: { size: 10 },
                            maxRotation: granularity === 'day' ? 45 : 0,
                            ...(maxTicksLimit ? { maxTicksLimit } : {}),
                            autoSkip: true,
                            autoSkipPadding: 16,
                        },
                        border: { display: false },
                    },
                    y: {
                        grid: { color: gridColor },
                        ticks: {
                            color: textColor,
                            font: { size: 10 },
                            callback: (v: any) => {
                                const n = Number(v)
                                if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`
                                if (n >= 1_000) return `$${(n / 1_000).toFixed(0)}K`
                                return `$${n}`
                            }
                        },
                        border: { display: false },
                        beginAtZero: true,
                    }
                }
            }
        } as any)

        return () => {
            if (chartRef.current) {
                chartRef.current.destroy()
                chartRef.current = null
            }
        }
    }, [labels, salesValues, compareValues, maxLen, granularity, includeReturns, comparisonLabel, colorMode])

    // Dismiss tooltip when clicking outside the canvas
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent | TouchEvent) => {
            if (canvasRef.current && chartRef.current) {
                if (!canvasRef.current.contains(e.target as Node)) {
                    chartRef.current.setActiveElements([])
                    if (chartRef.current.tooltip) {
                        chartRef.current.tooltip.setActiveElements([], {x: 0, y: 0})
                    }
                    chartRef.current.update()
                }
            }
        }
        document.addEventListener('mousedown', handleClickOutside)
        document.addEventListener('touchstart', handleClickOutside, { passive: true })
        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
            document.removeEventListener('touchstart', handleClickOutside)
        }
    }, [])

    const granularityLabel = granularity === 'month' ? 'Monthly' : granularity === 'week' ? 'Weekly' : 'Daily'

    return (
        <div className="chart-panel" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <div className="chart-panel-title" style={{ padding: '12px 16px 0', fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' }}>
                {granularityLabel} Performance
            </div>
            <div style={{ flex: 1, minHeight: 0, padding: '8px 12px 12px', position: 'relative' }}>
                {maxLen === 0
                    ? <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--color-text-tertiary)', fontSize: 'var(--text-sm)' }}>
                        No sales in this period
                      </div>
                    : <canvas ref={canvasRef} />}
            </div>
        </div>
    )
}
